<script setup lang="ts">
import { computed } from "vue";
import { weatherDescription } from "rainowls-shared";
import WeatherIconSimple from "@/components/weather/WeatherIconSimple.vue";
import { useUnitConversion } from "@/composables/useUnitConversion";
import type { TemperatureUnit } from "@/stores/settings.store";
import type { LocationWeatherSummary } from "@/utils/locationSummary";

const props = defineProps<{
  summary?: LocationWeatherSummary;
  loading: boolean;
  unit: TemperatureUnit;
  now: number;
}>();

const { convertTemperature, labels } = useUnitConversion(() => props.unit);

const description = computed(() => {
  const s = props.summary;
  if (!s)
    return props.loading
      ? "Loading Open-Meteo conditions"
      : "Open-Meteo conditions unavailable";
  const stale = s.stale || props.now - s.fetchedAt >= 10 * 60_000;
  return `${stale ? "Cached, possibly outdated: " : ""}Open-Meteo, ${weatherDescription(s.weatherCode)}, ${convertTemperature(s.temperature)}${labels.value.temperature}. Fetched ${new Date(s.fetchedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}.`;
});
</script>
<template>
  <span
    class="inline-flex w-[76px] shrink-0 items-center justify-end gap-1 text-xl leading-7 font-semibold text-text-primary tabular-nums"
    :title="description"
    :aria-label="description"
    role="img"
  >
    <template v-if="summary">
      <WeatherIconSimple
        :weather-code="summary.weatherCode"
        :is-day="summary.isDay"
        :size="26"
        aria-hidden="true"
      />
      <span aria-hidden="true"
        >{{ convertTemperature(summary.temperature) }}°</span
      >
    </template>
    <span
      v-else-if="loading"
      class="h-4 w-[58px] rounded-md bg-app-selected"
      aria-hidden="true"
    />
    <span v-else aria-hidden="true">—</span>
  </span>
</template>
